'use client';

import type { ProductReview } from '@eliadmin/core/src/api/products/types/product-types';
import { CmsImage } from '@eliadmin/core/src/components/dlz/ui/cms-image';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { RatingStars } from '@eliadmin/core/src/components/dlz/ui/rating-stars';
import { REVIEW_COPY } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-copy';
import { ReviewText } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-text';
import {
  ratingOf,
  reviewAuthor,
  reviewInitials,
} from '@eliadmin/core/src/components/dlz/product-detail/reviews/reviews';
import { useReviewFormat } from '@eliadmin/core/src/components/dlz/product-detail/reviews/use-product-reviews';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Figma „Review“ of the tab (60137:9282): initials avatar, name, date, stars, text and the photo. */
export function ReviewItem({ review, divider }: { review: ProductReview; divider: boolean }) {
  const format = useReviewFormat();
  const rating = ratingOf(review);
  const author = reviewAuthor(review);
  const initials = reviewInitials(review);

  return (
    <article className={cn('flex flex-col gap-4 pb-6', divider && 'border-b border-[#E2E4E7]')}>
      <header className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="dlz-body-s-medium flex size-11 shrink-0 items-center justify-center rounded-full bg-[#F5F6F7] text-[#0F1012]"
        >
          {initials || <Icon name="user" size={20} />}
        </span>
        <h3 className="dlz-body-s-medium min-w-0 flex-1 text-[#0F1012]">{author}</h3>
        <time dateTime={review.created_at} className="dlz-body-s shrink-0 text-[#6B7079]">
          {format.date(review.created_at)}
        </time>
      </header>
      <div className="flex flex-col gap-3">
        <RatingStars value={rating} label={REVIEW_COPY.summary.rating(String(rating))} />
        <ReviewText text={review.review} className="dlz-body-m text-[#4F545C]" />
      </div>
      {review.image ? (
        <div className="relative size-[88px] overflow-hidden rounded-xl bg-[#F5F6F7]">
          <CmsImage image={review.image} fallback={false} alt={author} sizes="88px" />
        </div>
      ) : null}
    </article>
  );
}
